import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween
} from '@cloudscape-design/components';
import { useContext, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { UserContext } from '../../context/authContext';
import { testStreamingApi } from '../../helpers/testStreamingApi';

export const StreamingTest = () => {
  const { token } = useContext(UserContext);
  const [prompt, setPrompt] = useState<string>('');
  const [response, setResponse] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [streamComplete, setStreamComplete] = useState<boolean>(false);

  const streamUrlConfigured = Boolean(process.env.REACT_APP_STREAM_URL);

  const handleReset = () => {
    setPrompt('');
    setResponse('');
    setError('');
    setStreamComplete(false);
  };

  const handleSubmit = async () => {
    if (!prompt.trim() || !token || !streamUrlConfigured) {
      return;
    }

    setError('');
    setResponse('');
    setStreamComplete(false);
    setIsStreaming(true);

    try {
      await testStreamingApi({
        token,
        prompt,
        onChunk: (text: string) => {
          setResponse((current) => `${current}${text}`);
        },
        onDone: () => {
          setStreamComplete(true);
        }
      });
    } catch (e) {
      const error = e as Error;
      if (axios.isAxiosError(error)) {
        setError(error.response?.data?.message || error.message);
      } else {
        setError(error.message);
      }
    } finally {
      setIsStreaming(false);
    }
  };

  return (
    <Container
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={handleReset} disabled={isStreaming}>
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={
                  isStreaming ||
                  !prompt.trim() ||
                  !token ||
                  !streamUrlConfigured
                }
                loading={isStreaming}
              >
                Start Stream
              </Button>
            </SpaceBetween>
          }
        >
          Streaming Test
        </Header>
      }
    >
      <SpaceBetween size="l">
        {!streamUrlConfigured ? (
          <Alert type="warning" header="Missing stream endpoint">
            Set `REACT_APP_STREAM_URL` in your environment before using this
            screen.
          </Alert>
        ) : null}
        {!token ? (
          <Alert type="warning" header="Missing auth token">
            Sign in before testing the streaming endpoint.
          </Alert>
        ) : null}
        {error ? (
          <Alert type="error" header="Streaming request failed">
            {error}
          </Alert>
        ) : null}

        <FormField
          label="Prompt"
          description="Enter the prompt that will be sent to the streaming endpoint."
        >
          <Input
            value={prompt}
            onChange={({ detail }) => setPrompt(detail.value)}
            placeholder="Ask the streaming endpoint something"
            disabled={isStreaming}
          />
        </FormField>

        <Container
          fitHeight
          header={
            <Header
              variant="h2"
              description="Chunks are appended here as they arrive."
            >
              Response
            </Header>
          }
        >
          <Box variant="div">
            <div
              className="min-h-[16rem] rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-900 [&_code]:rounded [&_code]:bg-slate-200 [&_code]:px-1 [&_code]:py-0.5"
            >
              {response ? (
                <ReactMarkdown>{response}</ReactMarkdown>
              ) : (
                'No streamed response yet.'
              )}
            </div>
          </Box>
          <Box margin={{ top: 's' }} color="text-body-secondary">
            {isStreaming
              ? 'Streaming in progress...'
              : streamComplete
                ? 'Stream complete.'
                : 'Ready.'}
          </Box>
        </Container>
      </SpaceBetween>
    </Container>
  );
};
